// DashboardModal.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Hooks
import useDashboardModalState from './useDashboardModalState';

// CSS
import './DashboardModal.css';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import Switch from 'components/switch/Switch';
import TextInput from 'components/textinput/TextInput';

/**
 * DashboardModal
 * 
 * This component allows the user to set a dashboard title and initial column count when
 * adding a dashboard, or edit the title for existing dashboards.
 * 
 */
const DashboardModal = React.memo(({
    isOpen,
    onClose,
    dashboard = null,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();
    const {
        modalState,
        formState,
        handlers,
    } = useDashboardModalState({
        isOpen,
        onClose,
        dashboard,
    });

    const columnOptionBaseStyle = {
        background: theme.backgroundMuted,
        border: `1px solid ${theme.status}`,
        color: theme.foreground,
    };

    // ----- RENDER ----- //

    return (
        <Modal
            iconKey="dashboard"
            title="Dashboard Settings"
            isOpen={isOpen}
            onClose={handlers.handleClose}
            width={isMobile ? '100%' : 520}
            height='auto'
        >
            <ModalForm>

                <ModalField>

                    <ModalLabel label="Title" />

                    <TextInput
                        value={formState.title}
                        placeholder="Dashboard title"
                        onChange={(event) => formState.setTitle(event.target.value)}
                        autoFocus
                    />

                    <ModalHint hint="Give your dashboard a descriptive title to distinguish it for viewers." />

                </ModalField>

                <ModalField>

                    <ModalLabel label="Description" />

                    <TextInput
                        expandable
                        maxLength={280}
                        multiline
                        placeholder="Optional dashboard description"
                        showCount
                        value={formState.description}
                        onChange={(event) => formState.setDescription(event.target.value)}
                    />

                    <ModalHint hint="Optional dashboard description to provide more context for viewers." />

                </ModalField>

                <ModalField>

                    <div className="dashboard-modal-switch-row">

                        <div
                            className="dashboard-modal-label"
                            style={{
                                color: theme.foreground,
                                marginBottom: 0,
                                paddingBottom: 0,
                                width: 'auto',
                            }}
                        >
                            Private
                        </div>

                        <div>
                            <Switch
                                checked={formState.isPrivate}
                                disabled={modalState.isSubmitting}
                                onChange={formState.setIsPrivate}
                            />
                        </div>

                    </div>

                </ModalField>

                {!modalState.isEditMode && (

                    <ModalField>

                        <ModalLabel label="Columns" />

                        <div className="dashboard-modal-column-options">
                            <button
                                type="button"
                                className={`dashboard-modal-column-option${formState.columnCount === 2 ? ' active' : ''}`}
                                style={{
                                    ...columnOptionBaseStyle,
                                    borderColor: formState.columnCount === 2 ? theme.foregroundMuted : theme.status,
                                    background: formState.columnCount === 2 ? theme.backgroundHover : theme.backgroundMuted,
                                }}
                                onClick={() => formState.setColumnCount(2)}
                            >
                                2
                            </button>
                            <button
                                type="button"
                                className={`dashboard-modal-column-option${formState.columnCount === 3 ? ' active' : ''}`}
                                style={{
                                    ...columnOptionBaseStyle,
                                    borderColor: formState.columnCount === 3 ? theme.foregroundMuted : theme.status,
                                    background: formState.columnCount === 3 ? theme.backgroundHover : theme.backgroundMuted,
                                }}
                                onClick={() => formState.setColumnCount(3)}
                            >
                                3
                            </button>

                        </div>

                        <ModalHint hint="Choose the number of columns for this dashboard layout." />

                    </ModalField>
                )}

            </ModalForm>

            <ModalButtons>

                <Button
                    iconKey="check"
                    label={modalState.isSubmitting
                        ? (modalState.isEditMode ? 'Saving...' : 'Creating...')
                        : (modalState.isEditMode ? 'Save Changes' : 'Create Dashboard')}
                    enabled={modalState.canSubmit}
                    onClick={handlers.handleSubmit}
                />

            </ModalButtons>

        </Modal>
    );
});

DashboardModal.displayName = 'DashboardModal';

export default DashboardModal;
